/**
 * Which of the camera-pan track's OFF-SCREEN buffer columns currently carry
 * blocks.
 *
 * The track deliberately mounts a buffer page each side of the visible window
 * (see `useDayWindow`) so a day/week step only translates. On an ordinary
 * calendar that buffer is free — the reference dataset's whole 25-day window is
 * ~91 blocks. On a DENSE one it is not: ~85 timed items a day turns one visible
 * week into ~1,800 mounted blocks, and the ~1,300 nobody can see cost seconds of
 * React on top of the ~600 that are actually on screen.
 *
 * So the buffer is armed by BLOCK BUDGET, not by column count, and it is armed
 * incrementally:
 *
 * - Columns arm OUTWARD from the visible window, nearest first.
 * - One step arms roughly `STEP_BLOCKS` blocks' worth of columns, so a sparse
 *   calendar arms its entire buffer in a single (free) step and a dense one
 *   arms about a column at a time — many short commits instead of one long,
 *   uninterruptible one.
 * - Arming stops once the off-screen buffer holds `FLANK_BLOCK_BUDGET` blocks,
 *   except for the innermost `MIN_FLANK` columns each side, which always arm so
 *   the finest pan (a one-day header drag / swipe) never mounts on the fly.
 *
 * Nothing here caps what a day SHOWS: a column is either off-screen-and-not-yet-
 * armed or rendered in full. The visible window is never subject to any of this.
 */

/** Blocks armed per staging step. */
export const STEP_BLOCKS = 60;
/**
 * How many blocks the off-screen buffer may hold in total.
 *
 * Sized so an ordinary calendar arms its ENTIRE buffer (the reference dataset's
 * whole 25-day window is ~91 blocks) and nothing about panning changes. A column
 * is the atomic unit of arming and cannot be split across frames, so on a dense
 * calendar — where one column alone is ~85 blocks, i.e. a dropped frame — every
 * buffer column past the forced ones buys smoothness for a pan that may never
 * happen at the price of jank the user gets for certain.
 */
export const FLANK_BLOCK_BUDGET = 150;
/** Buffer columns each side that arm regardless of budget. */
export const MIN_FLANK = 1;

export interface FlankArmingInput {
  /** Column key per index (the day's ISO date) — stable across a recenter, which
   *  renumbers indices while the armed set is still keyed by the old ones. */
  keys: readonly string[];
  /** Index of the first on-screen column. */
  firstVisibleCol: number;
  /** How many columns are on screen. */
  visibleCount: number;
  /** Blocks a column would mount. */
  costAt: (index: number) => number;
  /** Keys armed so far (may contain keys that have left the window). */
  armed: ReadonlySet<string>;
  /** Blocks the buffer may hold. Overridable for tests; production always uses
   *  `FLANK_BLOCK_BUDGET`.
   *
   *  Filling the REST of the buffer afterwards on `requestIdleCallback` was
   *  tried and removed: it put ~950 ms back onto `cold-start-week` and bought
   *  `calendar-week-nav` nothing, because repeated stepping leaves no idle and
   *  each step invalidates the direction the previous one pre-armed. */
  budget?: number;
}

function isOnScreen(i: number, firstVisibleCol: number, visibleCount: number): boolean {
  return i >= firstVisibleCol && i < firstVisibleCol + visibleCount;
}

/** Distance in columns from the visible window's nearer edge (1 = adjacent). */
export function flankDistance(i: number, firstVisibleCol: number, visibleCount: number): number {
  if (i < firstVisibleCol) return firstVisibleCol - i;
  return i - (firstVisibleCol + visibleCount - 1);
}

/**
 * The next armed set, or `null` when nothing changes.
 *
 * Arming is MONOTONIC within a window: a column that has blocks never loses them
 * because the view panned away from it. Unmounting an off-screen column's blocks
 * would cost as much as mounting them did — and would run its exit animations —
 * so the only pruning is of keys that have left the window entirely (those
 * columns already unmounted).
 *
 * **That includes the columns that are on screen NOW.** They are not "armed" in
 * the sense of needing to be staged — they render from `colDataByDate` directly
 * and cost nothing here — but they must be RETAINED, because the moment a step
 * moves them off screen they become flank columns, and a flank column that is not
 * in this set renders `EMPTY_COL`. Without retention every week step threw the
 * outgoing week's blocks away and the arming effect then re-mounted the nearest
 * of them: measured on the dense flat dataset, a step whose NET block count moved
 * by +19 did **343 mounts and 324 unmounts** — 667 element lifecycles, the bulk
 * of a 273 ms synchronous keydown handler. Net counts hide this completely; see
 * `tools/perf/probe-churn.mjs`.
 *
 * On-screen columns are deliberately NOT counted against the budget (they are
 * mounted regardless of what this decides). They start counting the moment they
 * go off screen, which is what stops the buffer growing without bound as the
 * user pans: retained columns fill the budget, so no NEW ones arm behind them.
 */
export function planFlankArming({ keys, firstVisibleCol, visibleCount, costAt, armed, budget = FLANK_BLOCK_BUDGET }: FlankArmingInput): Set<string> | null {
  const inWindow = new Set(keys);
  let spent = 0;
  const candidates: Array<{ index: number; key: string; dist: number; cost: number }> = [];
  const onScreen: string[] = [];
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i]!;
    if (isOnScreen(i, firstVisibleCol, visibleCount)) { onScreen.push(key); continue; }
    if (armed.has(key)) { spent += costAt(i); continue; }
    candidates.push({ index: i, key, dist: flankDistance(i, firstVisibleCol, visibleCount), cost: costAt(i) });
  }
  candidates.sort((a, b) => a.dist - b.dist || a.index - b.index);

  const add: string[] = [];
  let stepCost = 0;
  for (const c of candidates) {
    const forced = c.dist <= MIN_FLANK;
    if (!forced && spent + c.cost > budget) break;
    // Always take at least one column per step, so a single column costlier than
    // the step budget still arms (just alone, in its own commit).
    if (add.length > 0 && stepCost + c.cost > STEP_BLOCKS) break;
    add.push(c.key);
    stepCost += c.cost;
    spent += c.cost;
  }

  const next = new Set<string>();
  for (const key of armed) if (inWindow.has(key)) next.add(key);
  for (const key of add) next.add(key);
  for (const key of onScreen) next.add(key);
  // Identity is what the caller's `useState` compares, so returning an equal set
  // would re-render the grid for nothing on every arming pass.
  if (next.size === armed.size && [...next].every((key) => armed.has(key))) return null;
  return next;
}
